import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function EstadoValidoCreditoForm() {
    const [form, setForm] = useState({
        valor: '',
        credito: 'N'
    });
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    useEffect(() => {
        if (id) {
            cargarDatos();
        }
    }, [id]);

    const cargarDatos = async () => {
        try {
            setFetching(true);
            const response = await fetch(`${API_URL}/parametrizacion/estados-valido-credito/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setForm(data);
            }
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const method = id ? 'PUT' : 'POST';
            const url = id ? `${API_URL}/parametrizacion/estados-valido-credito/${id}` : `${API_URL}/parametrizacion/estados-valido-credito`;
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });

            if (response.ok) {
                toast.success(id ? 'Regla actualizada' : 'Regla creada');
                setTimeout(() => navigate('/parametrizacion/estados-valido-credito'), 1000);
            } else {
                throw new Error('Error al guardar');
            }
        } catch (error) {
            toast.error('Error al guardar la regla');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center h-64">
                <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            <div className="bg-slate-700 p-4 flex items-center gap-3 text-white">
                <button
                    onClick={() => navigate('/parametrizacion/estados-valido-credito')}
                    className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-lg font-semibold tracking-tight uppercase">
                    {id ? 'Editar Regla de Validación' : 'Nueva Regla de Validación'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                        <div className="ml-3">
                            <p className="text-xs text-blue-700">
                                Ingrese el código (Valor) que identifica al estado en el sistema (ej. PRS, ACT, INA).
                            </p>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Código de Estado (Valor)</label>
                    <input
                        type="text"
                        required
                        className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-teal-500 outline-none text-sm rounded-sm font-mono uppercase"
                        value={form.valor}
                        onChange={(e) => setForm({ ...form, valor: e.target.value.toUpperCase() })}
                        placeholder="Ej: PRS"
                        disabled={id} // Don't allow changing the code if editing
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-2">¿Permitir Solicitud de Crédito?</label>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="credito"
                                value="S"
                                checked={form.credito === 'S'}
                                onChange={(e) => setForm({ ...form, credito: e.target.value })}
                                className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-600 font-bold group-hover:text-teal-700 transition-colors">SÍ (Permitir)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name="credito"
                                value="N"
                                checked={form.credito === 'N'}
                                onChange={(e) => setForm({ ...form, credito: e.target.value })}
                                className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                            />
                            <span className="text-sm text-gray-600 font-bold group-hover:text-red-700 transition-colors">NO (Bloquear)</span>
                        </label>
                    </div>
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/estados-valido-credito')}
                        className="px-6 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 uppercase"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold uppercase rounded-sm shadow-md transition-all disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {id ? 'Actualizar Regla' : 'Guardar Regla'}
                    </button>
                </div>
            </form>
        </div>
    );
}
